import projectsApi from "@/api/v1/projects";
import router from "@/router/index";
import cloneDeep from "lodash.clonedeep";

class Project {
  constructor(project = {}) {
    this.campaigns = project.campaigns || null;
    this.tags = project.tags || null;
    this.files = project.files || null;
    this.finalFiles = project.finalFiles || null;
    this.chat = project.chat || null;
    this.history = project.history || null;
    this.copy = project.copy ?? null; // string
    this.description = project.description ?? null;
    // if user does not belong to project must not see entire card
    // therefore fields above have to be null
    this.createdAt = project.createdAt || null;
    this.deadline = project.deadline || "";
    this.discLocation = project.discLocation || "";
    this.locationEditable = project.locationEditable || "";
    this.editable = project.editable || false;
    this.endDate = project.endDate || null;
    this.hyperlink = project.hyperlink || "";
    this.id = project.id || null;
    this.lastStatusPerStep = project.lastStatusPerStep || [];
    this.media = project.media || [];
    this.members = project.members || [];
    this.name = project.name || "";
    this.orderers = project.orderers || [];
    this.managers = project.managers || [];
    this.priority = project.priority || "Standardowy";
    this.progressState = {
      state: project.progressState?.state || null,
      dismissed: project.progressState?.dismissed || false
    };
    this.projectType = project.projectType || "";
    this.printCoordination = project.printCoordination || false;
    this.printCoordinationEditable = project.printCoordinationEditable || false;
    this.softDeleted = project.softDeleted || false;
    this.status = project.status || "";
    this.template = {
      id: project.template?.id || null,
      type: project.template?.type || null
    };
    this.thumbnail = {
      filename: project.thumbnail?.filename || "",
      id: project.thumbnail?.id || null,
      url: project.thumbnail?.url || "",
      removed: project.thumbnail?.removed || false,
      originalFileUrl: project.thumbnail?.originalFileUrl || ""
    };
    this.userId = project.userId || null;
    this.youtubeLink = project.youtubeLink || null;
    this.youtubeThumbnailUrl = project.youtubeThumbnailUrl || null;
  }
}

export default {
  namespaced: true,

  state: {
    project: new Project(),
    projectChanges: [],
    projectName: "",
    actions: [],
    isDataLoading: false,
    isProcessing: false,
    isUserProcessing: false
  },

  mutations: {
    SET_PROJECT(state, projectData) {
      state.project = new Project(projectData);
    },

    CHANGE_LOADING_STATUS(state, status) {
      state.isDataLoading = status;
    },

    CHANGE_PROCESSING_STATUS(state, status) {
      state.isProcessing = status;
    },

    CHANGE_USER_PROCESSING_STATUS(state, status) {
      state.isUserProcessing = status;
    },

    SET_THUMBNAIL(state, data) {
      state.project.thumbnail = data;
    },

    CLEAR_YOUTUBE_THUMBNAIL(state) {
      state.project.youtubeThumbnailUrl = "";
    },

    SET_PROJECT_CHANGES(state, changes) {
      state.projectChanges = changes;
    },

    CLEAR_PROJECT_CHANGES(state) {
      state.projectChanges = [];
    },

    CLEAR_PROJECT_DATA(state) {
      state.project = new Project();
    },

    SET_PROJECT_STATUS(state, status) {
      state.project.status = status;
    },

    SET_PROJECT_ACTIONS(state, actions) {
      state.actions = actions;
    },

    DELETE_PROJECT_USER(state, userData) {
      state.project[userData.listType] = state.project[userData.listType].filter(
        user => user.id !== userData.id
      );
    },

    UPDATE_PROJECT_FILES(state, updatedFiles) {
      const copiedProject = cloneDeep(state.project);
      updatedFiles.forEach(updatedFile => {
        const fileIndex = copiedProject.files.findIndex(file => file.id === updatedFile.id);
        copiedProject.files[fileIndex] = updatedFile;
      });
      state.project = copiedProject;
    }
  },

  actions: {
    fetchProject({ commit }, projectId) {
      commit("CHANGE_LOADING_STATUS", true, { root: true });

      return new Promise(resolve => {
        projectsApi.projects
          .show(projectId)
          .then(response => {
            commit("SET_PROJECT", response.data);
            resolve();
          })
          .finally(() => {
            commit("CHANGE_LOADING_STATUS", false, { root: true });
          });
      });
    },

    updateProject({ commit, dispatch, state }, projectData) {
      commit("CHANGE_PROCESSING_STATUS", true);

      return projectsApi.projects
        .update(state.project.id, projectData)
        .then(response => {
          commit("SET_PROJECT", response.data);
          dispatch(
            "snackbar/showSnackbar",
            {
              message: ["Projekt zaktualizowany"],
              type: "success"
            },
            { root: true }
          );
          router.push({ name: "project-single", params: { id: state.project.id } });
        })
        .finally(() => {
          commit("CHANGE_PROCESSING_STATUS", false);
        });
    },

    uploadProjectFile({ dispatch, state }, fileFormData) {
      return new Promise((resolve, reject) => {
        projectsApi.files
          .create(state.project.id, fileFormData)
          .then(resp => {
            dispatch(
              "snackbar/showSnackbar",
              {
                message: ["Plik został dodany"],
                type: "success"
              },
              { root: true }
            );
            dispatch("fetchProject", state.project.id);
            resolve(resp);
          })
          .catch(() => {
            reject();
          });
      });
    },

    deleteProjectFiles({ commit, dispatch, state }, fileIds) {
      commit("CHANGE_PROCESSING_STATUS", true);

      return new Promise(resolve => {
        projectsApi.files
          .delete(state.project.id, fileIds)
          .then(() => {
            dispatch(
              "snackbar/showSnackbar",
              {
                message: ["Plik(i) zostały usunięte"],
                type: "success"
              },
              { root: true }
            );
            dispatch("fetchProject", state.project.id);
            resolve();
          })
          .finally(() => {
            commit("CHANGE_PROCESSING_STATUS", false);
          });
      });
    },

    selectThumbnail({ commit, dispatch }, params) {
      commit("CHANGE_LOADING_STATUS", true, { root: true });

      projectsApi.projects
        .setThumbnail(params)
        .then(resp => {
          commit("SET_THUMBNAIL", resp.data);
          commit("CLEAR_YOUTUBE_THUMBNAIL");

          commit("dialog/CLEAR_DIALOG", null, { root: true });

          dispatch(
            "snackbar/showSnackbar",
            {
              message: ["Ustawiono nową miniaturę projektu"],
              type: "success"
            },
            { root: true }
          );
        })
        .finally(() => {
          commit("CHANGE_LOADING_STATUS", false, { root: true });
        });
    },

    createProjectUser({ commit, dispatch, state }, userData) {
      commit("CHANGE_USER_PROCESSING_STATUS", true);

      return new Promise(resolve => {
        projectsApi.projects
          .createUser({ projectId: state.project.id, ...userData })
          .then(response => {
            dispatch(
              "snackbar/showSnackbar",
              {
                message: ["Użytkownik został dodany"],
                type: "success"
              },
              { root: true }
            );

            const data = {
              listType: userData.listType,
              user: response.data
            };
            dispatch("fetchProject", state.project.id);
            resolve();
          })
          .finally(() => {
            commit("CHANGE_USER_PROCESSING_STATUS", false);
          });
      });
    },

    deleteProjectUser({ commit, dispatch }, userData) {
      projectsApi.projects
        .deleteUser(userData.id, userData.listType)
        .then(() => {
          dispatch(
            "snackbar/showSnackbar",
            {
              message: ["Użytkownik został usunięty"],
              type: "success"
            },
            { root: true }
          );

          commit("DELETE_PROJECT_USER", userData);
        })
        .catch(err => {
          dispatch(
            "snackbar/showSnackbar",
            {
              message: err.response.data.errors,
              type: "error"
            },
            { root: true }
          );
        });
    },

    fetchProjectChanges({ commit, rootState }, id) {
      commit("CHANGE_LOADING_STATUS", true, { root: true });
      const { selectedFilters: filters, order } = rootState.filters;

      projectsApi
        .getChanges(id, { filters, order })
        .then(response => {
          commit("SET_PROJECT_CHANGES", response.data);
        })
        .finally(() => {
          commit("CHANGE_LOADING_STATUS", false, { root: true });
        });
    },

    fetchProjectActions({ commit }, projectId) {
      return new Promise(resolve => {
        projectsApi.projects.showActions(projectId).then(response => {
          commit("SET_PROJECT_ACTIONS", response.data);
          resolve();
        });
      });
    },

    makeProjectAction({ commit, dispatch, state }, { projectId, ...actionData }) {
      dispatch("changeActionStatus", true, { root: true });
      const id = projectId || state.project?.id;
      return projectsApi.projects
        .setAction(id, actionData)
        .then(response => {
          dispatch("dialog/clearDialog", null, { root: true });

          dispatch(
            "snackbar/showSnackbar",
            {
              message: ["Akcja wykonana"],
              type: "success"
            },
            { root: true }
          );

          commit("SET_PROJECT_ACTIONS", response.data.actions);
          dispatch("fetchProject", id);
        })
        .finally(() => {
          dispatch("changeActionStatus", false, { root: true });
        });
    },

    changeProjectStatus({ commit, dispatch, state }, status) {
      commit("CHANGE_PROCESSING_STATUS", true);
      const { id } = state.project;

      projectsApi
        .changeStatus(id, status)
        .then(response => {
          dispatch("fetchProjectActions", id);
          commit("SET_PROJECT", response.data);

          dispatch(
            "snackbar/showSnackbar",
            {
              message: ["Zmieniono status projektu"],
              type: "success"
            },
            { root: true }
          );
        })
        .finally(() => {
          commit("CHANGE_PROCESSING_STATUS", false);
        });
    },

    printReport({ commit }, projectData) {
      commit("CHANGE_LOADING_STATUS", true, { root: true });

      projectsApi
        .generateReport(projectData.id)
        .then(resp => {
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(new Blob([resp.data]));
          link.setAttribute("download", `Raport ${projectData.name.replaceAll(".", "_")}.pdf`);
          document.body.appendChild(link);
          link.click();
        })
        .finally(() => {
          commit("CHANGE_LOADING_STATUS", false, { root: true });
        });
    },

    changeFilesStatus({ commit }, { fileIds, status }) {
      commit("CHANGE_PROCESSING_STATUS", true);

      return projectsApi.files
        .changeStatus(status, fileIds)
        .then(response => {
          commit("UPDATE_PROJECT_FILES", response.data);
        })
        .finally(() => {
          commit("CHANGE_PROCESSING_STATUS", false);
        });
    },
    downloadProjectFiles({ state, commit }, fileIds) {
      commit("CHANGE_PROCESSING_STATUS", true);

      return projectsApi.files
        .downloadFiles(fileIds)
        .then(resp => {
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(
            new Blob([resp.data], { type: "application/zip" })
          );
          link.setAttribute(
            "download",
            `Pliki ${state.project.name.replaceAll(".", "_")} [${fileIds.length}]`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .finally(() => {
          commit("CHANGE_PROCESSING_STATUS", false);
        });
    },

    resetProjectChanges({ commit }) {
      commit("CLEAR_PROJECT_CHANGES");
    },

    resetProject({ commit, dispatch }) {
      commit("CLEAR_PROJECT_DATA");
      commit("SET_PROJECT_ACTIONS", []);
      dispatch("changeActionStatus", false, { root: true });
    }
  }
};
